/* Libraries / React Components / Node_Modules */
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import moment from "moment";
import { toastr } from 'react-redux-toastr';

/* Local Imports */
import { QUANTUM_API } from "../../utils/constant";
import Loader from "../resuableComponent/loader";

/* Data */
import { 
  fetchQuantumOverview, 
  fetchQuantumGraphData,
  downloadQuantumScanner,
  generateQuantumToken
} from "../../actions/quantumReadiness";

import QuantumPlanner from './QuantumPlanner';


// Helper function to get icon for encryption type
const getEncryptionIcon = (type) => {
  const iconMap = {
    'RSA': 'fa-lock',
    'AES': 'fa-shield',
    'ECC': 'fa-key',
    'DES': 'fa-lock',
    'Blowfish': 'fa-cog',
    'SHA': 'fa-lock',
    'MD5': 'fa-hashtag'
  };

  return iconMap[type] || 'fa-lock';
  //return `icon-${type.toLowerCase()}` || 'fa-lock';
};

const QuantumReadinessOverview = () => {
    const dispatch = useDispatch();
    const id = useSelector(
      (state) =>
        state?.summary?.localClient?._id || state?.summary?.clients?.data?.[0]?._id
    );
    const client = useSelector(
      (state) => state?.summary?.localClient || state?.summary?.clients?.data?.[0]
    );

    // Get quantum data from Redux store
    const overview = useSelector((state) => state?.quantumReadiness?.overview);
    const downloadStatus = useSelector((state) => state?.quantumReadiness?.download);

    const tokenInfo = useSelector((state) => state?.quantumReadiness?.token);

  // =============== State Management ===============
    // Pagination & Filtering
    const [isLoading, setIsLoading] = useState(true); // new
    const [activeTab, setActiveTab] = useState('overview');

    // Modal and Action States
    const [isModal, setModal] = useState("");

    // Quantum Specific States
    const [discoveredTechnologies, setDiscoveredTechnologies] = useState([]);
    const [isLoadingTechnologies, setIsLoadingTechnologies] = useState(true);
    const [expandedTech, setExpandedTech] = useState(null);

    const [tokenStatus, setTokenStatus] = useState(null);
    const [tokenValue, setTokenValue] = useState('');
    const [tokenCopied, setTokenCopied] = useState(false);

  // ====== COMPUTED VALUES & CONSTANTS ======
    // Default technologies configuration
    const defaultTechnologies = useMemo(() => [
      { name: 'RSA', count: 0, icon: getEncryptionIcon('RSA') },
      { name: 'AES', count: 0, icon: getEncryptionIcon('AES') },
      { name: 'ECC', count: 0, icon: getEncryptionIcon('ECC') },
      { name: 'DES', count: 0, icon: getEncryptionIcon('DES') },
      { name: 'Blowfish', count: 0, icon: getEncryptionIcon('Blowfish') },
      { name: 'SHA', count: 0, icon: getEncryptionIcon('SHA') },
      { name: 'MD5', count: 0, icon: getEncryptionIcon('MD5') }
    ], []);
    
    // Compute totals from overview data
    const totals = useMemo(() => {
      //console.log("Calculating totals from:", overview);
      if (!overview || !overview.summary) {
        console.warn("Missing overview or summary data");
        return {};
      }
      
      return {
        domainsCount: overview.summary.domainsCount || 0,
        systemsCount: overview.summary.systemsCount || 0,
        applicationsCount: overview.summary.applicationsCount || 0,
        filesCount: overview.summary.filesCount || 0,
        connectionsCount: overview.summary.connectionsCount || 0,
        codeCount: overview.summary.codeCount || 0,
        networkCount: overview.summary.networkCount || 0, // Using connectionsCount as fallback
        pqrReadiness: getPQRReadinessStatus(overview.score),
        lastUpdated: overview.lastUpdated ? moment(overview.lastUpdated).format('YYYY-MM-DD HH:mm:ss') : 'N/A'
      };
    }, [overview]);
    
    // Add this function to build the algorithm hierarchy
    const buildAlgorithmHierarchy = useMemo(() => {
      if (!overview?.categorizedData?.encryptionFound) return {};
      
      // Base algorithm types
      const baseTypes = {
        'RSA': [],
        'ECDHE': [],
        'DHE': [],
        'AES': [],
        'TLS': [],
        'SHA': [],
        'OTHER': []
      };
      
      // Group algorithms by base type
      Object.keys(overview.categorizedData.encryptionFound).forEach(algo => {
        const algoUpper = algo.toUpperCase();
        let matched = false;
        
        // Find which base type this algorithm belongs to
        for (const baseType of Object.keys(baseTypes)) {
          if (algoUpper === baseType || algoUpper.includes(baseType)) {
            if (!baseTypes[baseType].includes(algo)) {
              baseTypes[baseType].push(algo);
            }
            matched = true;
            break;
          }
        }
        
        // If no match, put in OTHER category
        if (!matched && algo !== 'OTHER') {
          baseTypes['OTHER'].push(algo);
        }
      });
      
      // Remove empty categories
      return Object.fromEntries(
        Object.entries(baseTypes).filter(([_, algorithms]) => algorithms.length > 0)
      );
    }, [overview?.categorizedData?.encryptionFound]);

  // =============== Effect Hooks ===============
    // Process encryption technologies from overview data
    useEffect(() => {
      setIsLoadingTechnologies(true);
      
      if (overview && overview.categorizedData && overview.categorizedData.encryptionFound) {
        try {
          // Group similar algorithms and calculate total counts
          const hierarchy = buildAlgorithmHierarchy;
          
          // Create aggregated technology entries
          const technologies = Object.entries(hierarchy).map(([category, algorithms]) => {
            // Calculate total count across all algorithms in this category
            const count = algorithms.reduce((sum, algo) => {
              return sum + (overview.categorizedData.encryptionFound[algo]?.length || 0);
            }, 0);
            
            return {
              name: category,
              count: count,
              icon: getEncryptionIcon(category),
              algorithms: algorithms // Store the specific algorithms for potential drill-down
            };
          });
          
          //console.log("Processing technologies:", technologies);
          setDiscoveredTechnologies(technologies);
        } catch (error) {
          console.error('Error processing encryption technologies:', error);
          setDiscoveredTechnologies([]);
        } finally {
          setIsLoadingTechnologies(false);
        }
      } else {
        setIsLoadingTechnologies(false);
        // Set default technologies when no data
        setDiscoveredTechnologies(defaultTechnologies);
      }
    }, [overview, buildAlgorithmHierarchy, defaultTechnologies]);

    // useEffect for fetching data
    useEffect(() => {
     // console.log("Fetching data for ID:", id);
      if (!id) {
        setIsLoading(false); // No ID, so stop loading
        return;
      }
      
      setIsLoading(true);
      
      // Direct calls - no promises needed
      fetchQuantumOverview(id, dispatch);
      fetchQuantumGraphData(id, dispatch);
      
      // Short timeout as fallback
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 3000);
      
      return () => clearTimeout(timer);
    }, [id, dispatch]);
    
    // Then use another useEffect to react to data changes
    useEffect(() => {
      if (overview) {
        //console.log("Overview data loaded:", overview);
        setIsLoading(false);
      }
    }, [overview]);

  // ========= Token Validation =========
    // Function to check token validity
    const checkExistingToken = useCallback(async () => {
      // Get organization-specific token
      const token = localStorage.getItem(`quantum_install_token_${id}`);
      if (!token) {
        setTokenStatus({ valid: false, message: "No active token" });
        setTokenValue(''); // Clear the token field
        return;
      }
      
      try {
        const response = await axios.post(`${QUANTUM_API}/check-download-token`, { token });
        setTokenStatus(response.data);
        
        // If token is invalid or expired, clear the field
        if (!response.data.valid) {
          setTokenValue('');
          localStorage.removeItem(`quantum_install_token_${id}`);
        } else {
          setTokenValue(token);
        }
      } catch (error) {
        console.error("Token check error:", error);
        setTokenStatus({ valid: false, message: "Invalid token" });
        setTokenValue('');
        localStorage.removeItem(`quantum_install_token_${id}`);
      }
    }, [id]);

    // Update the token input field when the Redux state changes
    useEffect(() => {
      // Check if we have a token in Redux state
      if (tokenInfo?.value && tokenInfo.organisationId === id) {
        setTokenValue(tokenInfo.value);
      } else {
        // Try to get from localStorage as fallback, using organization-specific key
        const savedToken = localStorage.getItem(`quantum_install_token_${id}`);
        
        if (savedToken) {
          checkExistingToken(); // This will verify and set the token if valid
        } else {
          setTokenValue('');
        }
      }
    }, [tokenInfo, id, checkExistingToken]);

    // Check token validity on component mount
    useEffect(() => {
      checkExistingToken();
    }, [tokenInfo, checkExistingToken]); // Re-check when tokenInfo changes


  // ========= Handlers / Functions =========
    // Modal handlers
    const addModal = () => {
      setModal("Form Wizard modal");
    };

    // Helper function to determine readiness status based on score
    function getPQRReadinessStatus(score) {
      if (!score && score !== 0) return 'Not Assessed';
      
      // Use the actual risk levels from your data
      if (score >= 80) return 'Ready';
      if (score >= 50) return 'Partial';
      if (score >= 30) return 'At Risk';
      return 'Critical';
    }

    // Download handler
    const handleDownload = () => {
      downloadQuantumScanner(id, dispatch);
    };

    // Function to toggle the expanded state of a tech box
    const toggleTechDetails = (techName) => {
      if (expandedTech === techName) {
        setExpandedTech(null); // collapse if already expanded
      } else {
        setExpandedTech(techName); // expand the clicked one
      }
    };

  // ====== Token Handlers ======
    const handleGenerateToken = useCallback(() => {
      generateQuantumToken(id, dispatch).then(() => {
        checkExistingToken(); // Re-check token status after generation
      });
    }, [id, dispatch, checkExistingToken]);
    
    const handleCopyToken = () => {
      // Use the tokenValue state which already contains the correct token
      if (tokenValue) {
        navigator.clipboard.writeText(tokenValue)
          .then(() => {
            setTokenCopied(true);
            setTimeout(() => setTokenCopied(false), 3000);
          })
          .catch(err => console.error('Failed to copy text: ', err));
      } else {
        // No token available
        toastr.warning('No Token', 'Please generate a token first');
      }
    };

    // Add this function with your other helper functions
    const maskToken = (token) => {
      if (!token || typeof token !== 'string') return "No installation token yet";
      
      // For short tokens, just return as is
      if (token.length <= 8) return token;
      
      // Get first 4 and last 4 characters
      const firstFour = token.substring(0, 4);
      const lastFour = token.substring(token.length - 4);
      
      // Calculate how many dots to show (max total length 58)
      const dotsCount = Math.min(40, token.length - 8); // Maximum 50 dots to keep total under 58
      const dots = "•".repeat(dotsCount);
      
      return `${firstFour}${dots}${lastFour}`;
    };
  // ====== Token Handlers END ======

    const renderTechnologyBox = (tech) => (
      <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12" key={tech.name}>
        <div 
          className={`card mb-2 ${expandedTech === tech.name ? 'expanded-tech' : ''}`}
          onClick={() => toggleTechDetails(tech.name)}
          style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
        >
          <div className="body p-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-1">{tech.name}</h6>
                <h5 className="mb-0">{tech.count}</h5>
                <small className="text-muted">
                  {tech.algorithms && tech.algorithms.length > 1 && (
                    <span>{tech.algorithms.length} variants</span>
                  )}
                </small>
              </div>
              <div className="d-flex flex-column align-items-center">
                <i className={`fa ${tech.icon} fa-2x text-muted`}></i>
                {/* Show expand/collapse icon */}
                <i 
                  className={`fa ${expandedTech === tech.name ? 'fa-chevron-up' : 'fa-chevron-down'} mt-2`} 
                  style={{ fontSize: '0.8rem' }}
                ></i>
              </div>
            </div>
            
            {/* Show algorithms when expanded */}
            {expandedTech === tech.name && overview?.categorizedData?.encryptionFound ? (
              <div className="mt-3 algorithm-details">
                <hr />
                <h6 className="mb-2">Algorithm Details</h6>
                <div className="algorithm-list" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {Object.keys(overview.categorizedData.encryptionFound)
                    .filter(algo => algo.toUpperCase().includes(tech.name.toUpperCase()))
                    .sort()
                    .map(algo => (
                      <div key={algo} className="algorithm-item mb-2">
                        <div className="d-flex justify-content-between">
                          <span style={{ fontWeight: 'bold' }}>{algo}</span>
                          <span className="badge badge-primary">{overview.categorizedData.encryptionFound[algo]?.length || 0}</span>
                        </div>
                        <small className="text-muted">Found in: {overview.categorizedData.encryptionFound[algo]?.slice(0, 3).join(', ')}
                          {overview.categorizedData.encryptionFound[algo]?.length > 3 ? '...' : ''}
                        </small>
                      </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );

  return (
    <div>
      {/* Main Content */}
      {isLoading ? (
        <Loader />
      ) : (
        <div className="data-container active">
          {/* Header Section */}
          <div className="row clearfix no_margin" style={{ marginTop: '20px' }}>
            <div className="col-sm-2"></div>
            <div className="col-sm-10">
              
           {/* Alert for no data - Discreet Version */}
            {!overview || !overview.summary ? (
              <div className="row">
                <div className="col-sm-10">
                  <div className="custom-alert">
                    <h5 className="custom-alert-heading">No Quantum Readiness Data Available</h5>
                    <p className="custom-alert-text">
                      There is no quantum readiness data available for this account yet. 
                      To get started, download the PQR Scanner application and run scans on your systems.
                    </p>
                    <p className="custom-alert-text mb-0">
                      If you have already run scans, please check back later.
                      The data will be updated once the scans are processed.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}

            {/* NB! Add this CSS later to main CSS file */}
            <style>{`
              .custom-alert {
                background-color: rgba(209, 236, 250, 0.6);
                border-left: 3px solid rgba(23, 162, 184, 0.7);
                border-radius: 4px;
                padding: 15px 18px;
                margin-bottom: 20px;
              }
              
              .custom-alert-heading {
                font-size: 0.95rem;
                font-weight: 500;
                color: rgba(33, 37, 41, 0.85);
                margin-bottom: 10px;
              }
              
              .custom-alert-text {
                font-size: 0.85rem;
                color: rgba(33, 37, 41, 0.75);
                margin-bottom: 10px;
              }
            `}</style>

            {/* TAB Selector - Overview vs. Planner */}
            <div className="row">
              <div className="col-sm-10">
                <div className="content-tabs d-flex mb-3">
                  <button
                    className={`btn ${activeTab === 'overview' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setActiveTab('overview')}
                    style={{ 
                      flex: 1, 
                      borderRadius: '4px 0 0 4px',
                      borderRight: activeTab === 'planner' ? '1px solid #ccc' : 'none'
                    }}
                  >
                    <i className="fa fa-chart-bar mr-2"></i>
                    Overview
                  </button>
                  
                  <button
                    className={`btn ${activeTab === 'planner' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setActiveTab('planner')}
                    style={{ 
                      flex: 1,
                      borderRadius: '0 4px 4px 0',
                      borderLeft: activeTab === 'overview' ? '1px solid #ccc' : 'none'
                    }}
                  >
                    <i className="fa fa-calendar-check mr-2"></i>
                    Planner
                  </button>
                </div>
              </div>
            </div>

          {/* Conditional content based on active tab */}
          {activeTab === 'overview' ? (
            <>
              {/* Top Row */}
              <div className="row">
                {/* Description Section - Left Side  */}
                <div className="col-sm-6">
                  <div className="card mb-4">
                    <div className="card-body">
                      <h4 className="card-title">PQR Scanner</h4>
                      <p className="card-text">
                        As quantum computing advances, the threat to current cryptographic systems grows. 
                        Post-Quantum Cryptography (PQC) aims to develop algorithms resistant to quantum attacks, 
                        ensuring the security of sensitive data.
                        Organizations must transition to PQC to protect against the "Harvest Now, Decrypt Later" threat, 
                        where encrypted data captured today could be decrypted by quantum computers in the future.
                        <br></br><br></br><br></br><br></br>
                        To assist in this transition, we offer a scanning tool that can be run on your PC. 
                      </p>
                      <p className="card-text">
                        This tool evaluates your current encryption technologies and assesses your readiness for the post-quantum era.
                        <br></br>Download it now and conduct scans to ensure your systems are quantum-safe!<br></br><br></br>
                      </p>
                      
                      
                      {/* Download Function */}
                      {/*<button
                        className="btn btn-primary"
                        onClick={handleDownload}
                        disabled={downloadStatus?.downloading}
                        style={{ float: 'right'}}
                      >
                        {downloadStatus?.downloading ? (
                          <>
                            <i className="fa fa-spinner fa-spin mr-2"></i>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <i className="fa fa-download mr-2"></i>
                            Download Application
                          </>
                        )}
                      </button>*/}
                      
                      {/* OPTION 1: Download and token in one row */}
                      <div className="row mt-4 align-items-center">
                        <div className="col-md-8">
                          <div className="input-group">
                            <input 
                              type="text" 
                              className="form-control" 
                              value={tokenValue ? maskToken(tokenValue) : "No installation token generated"} 
                              readOnly
                              style={{ 
                                backgroundColor:'rgba(57, 57, 57, 0.2)',
                                color: tokenStatus?.valid ? 'inherit' : '#d9534f' // Red text for invalid token
                              }}
                            />
                            <div className="input-group-append">
                              <button 
                                className="btn btn-outline-secondary" 
                                type="button" 
                                onClick={handleCopyToken}
                                disabled={!tokenValue || !tokenStatus?.valid}
                                title="Copy token to clipboard"
                              >
                                <i className="fa fa-copy mr-1"></i> Copy
                              </button>
                              <button 
                                className="btn btn-outline-primary" 
                                type="button" 
                                onClick={handleGenerateToken}
                                disabled={tokenInfo?.generating}
                                title={tokenStatus?.valid ? "Generate new token" : "Generate token"}
                              >
                                {tokenInfo?.generating ? (
                                  <>
                                    <i className="fa fa-spinner fa-spin mr-1"></i> Generating...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa fa-refresh mr-1"></i> {tokenStatus?.valid ? "Regenerate" : "Generate"}
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                          {tokenCopied && (
                            <small className="text-success mt-1 d-block">
                              <i className="fa fa-check-circle mr-1"></i> Token copied to clipboard
                            </small>
                          )}
                          {tokenStatus?.valid === false && tokenStatus?.message && (
                            <small className="text-danger mt-1 d-block">
                              <i className="fa fa-exclamation-circle mr-1"></i> {tokenStatus.message}
                            </small>
                          )}

                          {tokenStatus?.valid && tokenStatus?.expiresAt && (
                            <small className="text-muted mt-1 d-block">
                              Expires: {moment(tokenStatus.expiresAt).format('DD-MM-YYYY HH:mm')} 
                            </small>
                          )}                        
                        </div>
                        <div className="col-md-4 text-right">
                          <button
                            className="btn btn-primary"
                            onClick={handleDownload}
                            disabled={downloadStatus?.downloading}
                          >
                            {downloadStatus?.downloading ? (
                              <>
                                <i className="fa fa-spinner fa-spin mr-2"></i>
                                Downloading...
                              </>
                            ) : (
                              <>
                                <i className="fa fa-download mr-2"></i>
                                Download Application
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                            
                            
                      {/*OPTION 2: Token container with increased margin */}
                      {/*<div className="token-container mb-5" style={{ marginBottom: '30px' }}>
                        <label className="font-weight-bold mb-2">Authentication Token</label>
                        <div className="input-group">
                          <input 
                            type="text" 
                            className="form-control" 
                            value={tokenValue || "install/download token"} 
                            readOnly
                            style={{ backgroundColor: '#f8f9fa' }}
                          />
                          <div className="input-group-append">
                            <button 
                              className="btn btn-outline-secondary" 
                              type="button" 
                              onClick={handleCopyToken}
                              title="Copy token to clipboard"
                            >
                              <i className="fa fa-copy mr-1"></i> Copy
                            </button>
                            <button 
                              className="btn btn-outline-primary" 
                              type="button" 
                              onClick={handleGenerateToken}
                              title="Generate new token"
                            >
                              <i className="fa fa-refresh mr-1"></i> Generate
                            </button>
                          </div>
                        </div>
                        {tokenCopied && (
                          <small className="text-success mt-1 d-block">
                            <i className="fa fa-check-circle mr-1"></i> Token copied to clipboard
                          </small>
                        )}
                      </div>*/}

                      {/* Download button with more separation */}
                      {/*<div className="text-right mt-4 pt-3" style={{ borderTop: '1px solid #eee' }}>
                        <button
                          className="btn btn-primary btn-lg"
                          onClick={handleDownload}
                          disabled={downloadStatus?.downloading}
                        >
                          {downloadStatus?.downloading ? (
                            <>
                              <i className="fa fa-spinner fa-spin mr-2"></i>
                              Downloading...
                            </>
                          ) : (
                            <>
                              <i className="fa fa-download mr-2"></i>
                              Download Application
                            </>
                          )}
                        </button>
                      </div>*/}



                      {/* OPTION 3: Token section in its own card */}
                      {/* <div className="card mb-4 mt-3">
                        <div className="card-body">
                          <h6 className="card-title">Authentication Token</h6>
                          <p className="card-text text-muted small">
                            Generate and copy your authentication token to use with the PQR Scanner application
                          </p>
                          
                          <div className="input-group mt-3">
                            <input 
                              type="text" 
                              className="form-control" 
                              value={tokenValue || "install/download token"} 
                              readOnly
                              style={{ backgroundColor: '#f8f9fa' }}
                            />
                            <div className="input-group-append">
                              <button 
                                className="btn btn-outline-secondary" 
                                type="button" 
                                onClick={handleCopyToken}
                                title="Copy token to clipboard"
                              >
                                <i className="fa fa-copy mr-1"></i> Copy
                              </button>
                              <button 
                                className="btn btn-outline-primary" 
                                type="button" 
                                onClick={handleGenerateToken}
                                title="Generate new token"
                              >
                                <i className="fa fa-refresh mr-1"></i> Generate
                              </button>
                            </div>
                          </div>
                          {tokenCopied && (
                            <small className="text-success mt-1 d-block">
                              <i className="fa fa-check-circle mr-1"></i> Token copied to clipboard
                            </small>
                          )}
                        </div>
                      </div>*/}

                    </div>
                  </div>
                </div>

                {/* Counter Boxes - Right Side */}
                <div className="col-sm-4">
                <div className="row">
                {/* Counter Box 1 */}
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <div className="card mb-2" onClick={() => window.location.href = '/quantum/domain'}>
                    <div className="body" style={{ cursor: 'pointer' }}>
                      <div className="row clearfix">
                        <div className="col-12">
                          <h5 className="mb-0">{totals.domainsCount || 0}</h5>
                          <small className="text-muted color-white">Domains</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Counter Box 2 */}
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <div className="card mb-2" onClick={() => window.location.href = '/quantum/system'}>
                    <div className="body" style={{ cursor: 'pointer' }}>
                      <div className="row clearfix">
                        <div className="col-12">
                          <h5 className="mb-0">{totals.systemsCount || 0}</h5>
                          <small className="text-muted color-white">Systems</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Counter Box 3 */}
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <div className="card mb-2" onClick={() => window.location.href = '/quantum/application'}>
                    <div className="body" style={{ cursor: 'pointer' }}>
                      <div className="row clearfix">
                        <div className="col-12">
                          <h5 className="mb-0">{totals.applicationsCount || 0}</h5>
                          <small className="text-muted color-white">Applications</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Counter Box 4 */}
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <div className="card mb-2" onClick={() => window.location.href = '/quantum/file'}>
                    <div className="body" style={{ cursor: 'pointer' }}>
                      <div className="row clearfix">
                        <div className="col-12">
                          <h5 className="mb-0">{totals.filesCount || 0}</h5>
                          <small className="text-muted color-white">Files</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Counter Box 5 */}
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <div className="card mb-2" onClick={() => window.location.href = '/quantum/traffic'}>
                    <div className="body" style={{ cursor: 'pointer' }}>
                      <div className="row clearfix">
                        <div className="col-12">
                          <h5 className="mb-0">{totals.connectionsCount || 0}</h5>
                          <small className="text-muted color-white">Traffic Connections</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                 {/* Counter Box 6 */}
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <div className="card mb-2" onClick={() => window.location.href = '/quantum/code'}>
                    <div className="body" style={{ cursor: 'pointer' }}>
                      <div className="row clearfix">
                        <div className="col-12">
                          <h5 className="mb-0">{totals.codeCount || 0}</h5>
                          <small className="text-muted color-white">Code Records</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Counter Box 7 */}
                <div className="col-xl-6 col-lg-6 col-md-12 col-sm-12">
                  <div className="card mb-2" onClick={() => window.location.href = '/quantum/code'}>
                    <div className="body" style={{ cursor: 'pointer' }}>
                      <div className="row clearfix">
                        <div className="col-12">
                          <h5 className="mb-0">{totals.networkCount || 0}</h5>
                          <small className="text-muted color-white">Networks</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                </div>
                </div>
              </div>

                {/* Middle Row - PQR Status Indicator and Recommendations */}
                <div className="row mt-4">
                  <div className="col-sm-10">
                    <div className="card">
                      <div className="card-body">
                        <div className="d-flex flex-wrap">
                          {/* PQR Status Left Side */}
                          <div className="status-container" style={{ flex: '0 0 250px', marginRight: '20px' }}>
                            <div className="d-flex align-items-center">
                              <div className={`status-light ${
                                !totals?.pqrReadiness ? 'yellow' :
                                totals?.pqrReadiness === 'ready' ? 'green' : 'red'
                                }`}>
                              </div>
                              <div className="status-text ml-3">
                                <h6 className="mb-1">Post Quantum Readiness</h6>
                                <p className={!totals?.pqrReadiness ? 'text-muted mb-0' : 'text-white mb-0'}>
                                  {totals?.pqrReadiness || 'Not Assessed'}
                                  {overview?.score !== undefined && (
                                    <span className="ml-2">({overview.score})</span>
                                  )}
                                </p>
                                {overview?.summary?.riskLevel && (
                                  <small className="text-muted">
                                    Risk Level: {overview?.summary.riskLevel}
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Recommendations Right Side */}
                          {overview?.summary?.recommendations && 
                            overview.summary.recommendations.length > 0 && (
                              <div className="recommendations-container" style={{ flex: '1' }}>
                                <ul className="recommendations-list" style={{ paddingLeft: '10.5rem', margin: 0, fontSize: '11px' }}>
                                  {overview?.summary.recommendations.slice(0, 5).map((rec, idx) => (
                                    <li key={idx} className="mb-1">{rec}</li>
                                  ))}
                                
                                </ul>
                              </div>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                <style>{`
                    .status-indicator {
                      padding: 15px;
                    }
                    .status-light {
                      width: 25px;
                      height: 25px;
                      border-radius: 50%;
                      position: relative;
                      box-shadow: 0 0 10px rgba(0,0,0,0.2);
                    }
                    .status-light::after {
                      content: '';
                      position: absolute;
                      width: 100%;
                      height: 100%;
                      border-radius: 50%;
                      animation: pulse 2s infinite;
                      top: 0;
                      left: 0;
                    }
                    .green { 
                      background-color: #2ecc71;
                      border: 2px solid #27ae60;
                    }
                    .yellow { 
                      background-color: #f1c40f;
                      border: 2px solid #f39c12;
                    }
                    .yellow::after {
                      background-color: #f1c40f;
                    }
                    .red { 
                      background-color: #e74c3c;
                      border: 2px solid #c0392b;
                    }
                    .red::after {
                      background-color: #e74c3c;
                    }
                    @keyframes pulse {
                      0% {
                        transform: scale(1);
                        opacity: 0.7;
                      }
                      50% {
                        transform: scale(1.6);
                        opacity: 0;
                      }
                      100% {
                        transform: scale(1);
                        opacity: 0;
                      }
                    }
                    .ml-3 {
                      margin-left: 15px;
                    }
                    /* Styling for tech boxes */
                    .card {
                      transition: all 0.3s ease;
                    }
                    
                    .card:hover {
                      transform: translateY(-3px);
                      box-shadow: 0 5px 15px rgba(0,0,0,0.1);
                    }
                    
                    .expanded-tech {
                      box-shadow: 0 8px 20px rgba(0,0,0,0.2);
                    }
                    
                    .recommendations-list li {
                      margin-bottom: 8px;
                      line-height: 1.3;
                    }
                    
                    .algorithm-item {
                      padding: 8px;
                      border-bottom: 1px solid #eee;
                    }
                    
                    .algorithm-item:last-child {
                      border-bottom: none;
                    }
                  `}</style>
              </div> 

              {/* Encryption Technologies Section */}
              <div className="row mt-4">
                <div className="col-sm-10">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h4 className="card-title">Encryption Technologies Discovered</h4>
                                <small className="text-muted">
                                    Last scan: {overview?.lastUpdated ? moment(overview.lastUpdated).format('DD-MM-YYYY HH:mm:ss') : 'N/A'}
                                </small>
                            </div>
                            <div className="row">
                                {isLoadingTechnologies ? (
                                    <div className="col-12 text-center">
                                        <Loader />
                                    </div>
                                ) : (
                                    <>
                                        {(discoveredTechnologies.length > 0 
                                            ? discoveredTechnologies
                                            : defaultTechnologies.map(tech => ({
                                                ...tech,
                                                count: totals?.[tech.name.toLowerCase()] || 0
                                            }))
                                        ).map(tech => renderTechnologyBox(tech))}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
              </div>
            </>
        ) : (
          /* Planner Tab Content */
          <div className="row">
            <div className="col-sm-10">
              <QuantumPlanner 
                id={id} 
                client={client}
                overview={overview}
              />
            </div>
          </div>
        )} 
            </div>
          </div>

          {/* Modal */}
          <div className={`modal fade Form-Wizard-modal ${isModal === "Form Wizard modal" ? "d-block show" : ""}`}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                {/* Modal content here */}
                <div className="modal-header">
                  <h5 className="modal-title">Quantum Readiness Recommendations</h5>
                  <button 
                    type="button" 
                    className="close" 
                    onClick={() => setModal("")}
                  >
                    <span>×</span>
                  </button>
                </div>
                <div className="modal-body">
                  {overview?.overallReadiness?.recommendations && (
                    <div>
                      <p>
                        <strong>Risk Level: </strong> 
                        <span className={
                          overview.overallReadiness.riskLevel === "High" ? "text-danger" :
                          overview.overallReadiness.riskLevel === "Medium" ? "text-warning" : 
                          "text-success"
                        }>
                          {overview.overallReadiness.riskLevel}
                        </span>
                      </p>
                      <div className="alert alert-info">
                        Your current quantum readiness score is <strong>{overview.score}</strong>. 
                        Based on our analysis, you should address the following recommendations:
                      </div>
                      <ul className="list-group">
                        {overview.overallReadiness.recommendations.map((rec, idx) => (
                          <li key={idx} className="list-group-item">
                            <i className="fa fa-check-circle text-primary mr-2"></i>
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModal("")}>
                    Close
                  </button>
                  <button type="button" className="btn btn-primary" onClick={() => window.location.href = '/quantum/details'}>
                    View Detailed Report
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default QuantumReadinessOverview;
